/**
 * 파츠 합성 — 머리처럼 작고 중요한 부위를 **따로 찍은 클로즈업 8뷰**로 다시 만들어 본체에 붙인다.
 *
 * 왜 필요한가: 전신 1024² 이미지에서 얼굴은 세로 120px 남짓이다. 헐 해상도를 올려도 입력에 없는
 * 정보는 안 나온다 — 눈·코·입이 몇 픽셀이니 아무리 잘 구워도 뭉갠 그림이 된다. 머리만 찍은 1024²
 * 여덟 장이면 같은 얼굴이 800px 로 온다(≈6.7배, 면적 45배). 형상도 텍스처도 그만큼 살아난다.
 *
 * 무엇을 맞추는가: **머리통의 폭**. 처음엔 목 폭을 맞췄는데 어긋났다 — 전신 그림의 잘록한 곳은
 * 옷깃 위 목이고, 클로즈업의 잘록한 곳은 옷깃이 턱을 덮은 자리라 서로 다른 부위였다(실측: 폭 기준
 * 0.35 vs 높이 기준 0.23, 39% 차이. 작은 쪽을 쓰면 새 머리가 본체 머리 **안에** 들어가 안 보인다).
 * 머리통의 최대 폭은 두 그림에서 같은 것을 가리킨다 — 그걸로 배율을 잡고, 그 최대 폭이 나오는
 * 높이를 세로 기준점으로 쓴다. 목은 이음매(스키닝·오므리기·아래 자르기)로만 쓴다.
 *
 * 본체 머리는 지우지 않고 **안으로 오므린다**. 구멍을 내면 이음매가 보이고, 그냥 두면 새 머리를
 * 뚫고 나온다. 목 위 정점을 머리 축 쪽으로 12% 당기면 새 머리 안에 완전히 숨는다.
 */

import * as THREE from 'three';
import type { ViewImage } from '../turnaround/views.ts';

/** 실루엣의 높이별 폭(월드 단위, 높이 1 기준). t=0 바닥, t=1 꼭대기. */
export function widthProfile(view: ViewImage, samples = 256): Float32Array {
  const { mask, bbox, aspect } = view;
  const bw = Math.max(1, bbox.x1 - bbox.x0);
  const bh = Math.max(1, bbox.y1 - bbox.y0);
  const out = new Float32Array(samples + 1);
  const cols = 192;
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const yi = Math.round(bbox.y1 - t * bh);
    if (yi < 0 || yi >= mask.height) continue;
    let n = 0;
    for (let c = 0; c <= cols; c++) {
      const xi = Math.round(bbox.x0 + (c / cols) * bw);
      if (xi < 0 || xi >= mask.width) continue;
      if (mask.data[yi * mask.width + xi]! >= 0.5) n++;
    }
    out[i] = (n / cols) * aspect; // 월드 폭 (높이 1 기준)
  }
  return out;
}

/** [t0,t1] 구간에서 폭이 가장 좁은 t. 잡음에 흔들리지 않게 5탭 평균. */
function narrowest(profile: Float32Array, t0: number, t1: number): { t: number; w: number } {
  const n = profile.length - 1;
  const i0 = Math.max(2, Math.round(t0 * n)), i1 = Math.min(n - 2, Math.round(t1 * n));
  let best = i0, bestW = Infinity;
  for (let i = i0; i <= i1; i++) {
    const w = (profile[i - 2]! + profile[i - 1]! + profile[i]! + profile[i + 1]! + profile[i + 2]!) / 5;
    if (w > 1e-4 && w < bestW) { bestW = w; best = i; }
  }
  return { t: best / n, w: bestW };
}

/** 실루엣이 있는 가장 높은 t (머리 꼭대기). */
function topOf(profile: Float32Array, minW = 0.004): number {
  const n = profile.length - 1;
  for (let i = n; i >= 0; i--) if (profile[i]! > minW) return i / n;
  return 1;
}

/**
 * 헐 ↔ 오브젝트 좌표 변환. 헐은 높이 1·바닥 0·x,z 중심 0 이고, 오브젝트는 높이 2·원점 중심이다
 * (`buildTurnaroundHull` 끝의 scale(2)+translate). p = (P − offset) · scale, P = p / scale + offset.
 * 파츠를 붙일 때 이 변환을 빼먹으면 머리가 몸 한가운데에 박힌다(2026-09-04 실측: 파츠가 안 보였다).
 */
export interface HullFrame { offset: THREE.Vector3; scale: number }

const toHull = (P: THREE.Vector3, f: HullFrame) => P.clone().sub(f.offset).multiplyScalar(f.scale);

export interface AttachFit {
  /** 파츠 헐 좌표 → 본체 헐 좌표: p' = (p - [0, partAnchorT, 0]) * scale + [0, bodyAnchorT, dz] */
  scale: number;
  /** 머리통이 가장 넓은 높이 — 세로 기준점 */
  partAnchorT: number;
  bodyAnchorT: number;
  /** 목 — 이음매(스키닝 경계·본체 오므리기 시작·아래 자르기) */
  partNeckT: number;
  bodyNeckT: number;
  dz: number;
  /** 진단용 */
  detail: { byHeadWidth: number; byNeckWidth: number; byHeight: number; bodyHeadW: number; partHeadW: number; topAfter: number };
}

/** [t0,t1] 에서 가장 넓은 t. */
function widest(profile: Float32Array, t0: number, t1: number): { t: number; w: number } {
  const n = profile.length - 1;
  const i0 = Math.max(0, Math.round(t0 * n)), i1 = Math.min(n, Math.round(t1 * n));
  let best = i0, bestW = -1;
  for (let i = i0; i <= i1; i++) if (profile[i]! > bestW) { bestW = profile[i]!; best = i; }
  return { t: best / n, w: bestW };
}

/**
 * 머리 맞추기. 배율은 **머리통 최대 폭 비**로 잡는다. 목 폭 비·높이 비도 함께 재서 진단에 남긴다 —
 * 셋이 크게 어긋나면 두 그림이 같은 캐릭터가 아니거나 클로즈업이 잘렸다는 신호다.
 */
export function fitPartToBody(
  bodyFront: ViewImage,
  partFront: ViewImage,
  bodyGeometry: THREE.BufferGeometry,
  bodyFrame: HullFrame,
): AttachFit {
  const bp = widthProfile(bodyFront);
  const pp = widthProfile(partFront);
  const bodyNeck = narrowest(bp, 0.78, 0.93);
  const bodyTop = topOf(bp);
  // 머리 클로즈업: 목은 아래쪽 절반의 최솟값(턱 아래 ~ 옷깃 위).
  const partNeck = narrowest(pp, 0.08, 0.62);
  const partTop = topOf(pp);

  // 머리통 = 목 위. 그 최대 폭이 두 그림에서 같은 것을 가리킨다.
  const bodyHead = widest(bp, bodyNeck.t, bodyTop);
  const partHead = widest(pp, partNeck.t, partTop);
  const byHeadWidth = partHead.w > 1e-4 ? bodyHead.w / partHead.w : 1;
  const byNeckWidth = partNeck.w > 1e-4 ? bodyNeck.w / partNeck.w : byHeadWidth;
  const byHeight = partTop - partNeck.t > 1e-3 ? (bodyTop - bodyNeck.t) / (partTop - partNeck.t) : byHeadWidth;
  const scale = byHeadWidth;

  // 깊이: 본체의 목 위 정점 z 평균 — 머리가 몸 중심보다 앞뒤로 밀려 있을 수 있다.
  const pos = bodyGeometry.getAttribute('position') as THREE.BufferAttribute;
  const tmp = new THREE.Vector3();
  let zs = 0, n = 0;
  for (let v = 0; v < pos.count; v++) {
    tmp.fromBufferAttribute(pos, v);
    const p = toHull(tmp, bodyFrame);
    if (p.y < bodyNeck.t) continue;
    zs += p.z; n++;
  }
  const dz = n > 0 ? zs / n : 0;

  return {
    scale,
    partAnchorT: partHead.t,
    bodyAnchorT: bodyHead.t,
    partNeckT: partNeck.t,
    bodyNeckT: bodyNeck.t,
    dz,
    detail: {
      byHeadWidth, byNeckWidth, byHeight,
      bodyHeadW: bodyHead.w, partHeadW: partHead.w,
      topAfter: bodyHead.t + (partTop - partHead.t) * scale,
    },
  };
}

/**
 * 파츠 지오메트리를 **본체 오브젝트 좌표**로 옮긴다(제자리 수정).
 * 파츠 오브젝트 → 파츠 헐 → (목 맞추기) → 본체 헐 → 본체 오브젝트. 목 아래로는 keepBelowNeck(본체 헐 단위)
 * 까지만 남기고 그 아래 정점은 그 높이에 눌러 몸 안에 묻는다 — 잘라 구멍을 내는 것보다 이음매가 안 보인다.
 */
export function transformPart(
  geometry: THREE.BufferGeometry,
  fit: AttachFit,
  partFrame: HullFrame,
  bodyFrame: HullFrame,
  keepBelowNeck = 0.06,
): void {
  const pos = geometry.getAttribute('position') as THREE.BufferAttribute;
  // 본체 목 밑 keepBelowNeck(본체 헐 단위)에 해당하는 파츠 높이. 그 아래 정점은 거기 눌러 몸 안에 묻는다.
  const cutT = fit.partAnchorT + (fit.bodyNeckT - keepBelowNeck - fit.bodyAnchorT) / Math.max(fit.scale, 1e-6);
  const tmp = new THREE.Vector3();
  for (let v = 0; v < pos.count; v++) {
    tmp.fromBufferAttribute(pos, v);
    const p = toHull(tmp, partFrame);
    const hx = p.x * fit.scale;
    const hy = (Math.max(p.y, cutT) - fit.partAnchorT) * fit.scale + fit.bodyAnchorT;
    const hz = p.z * fit.scale + fit.dz;
    pos.setXYZ(
      v,
      hx / bodyFrame.scale + bodyFrame.offset.x,
      hy / bodyFrame.scale + bodyFrame.offset.y,
      hz / bodyFrame.scale + bodyFrame.offset.z,
    );
  }
  pos.needsUpdate = true;
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}

/** 본체 오브젝트 좌표에서의 목 높이 / 머리 축(x,z). 스키닝·오므리기가 쓴다. */
export function anchorsInObject(fit: AttachFit, bodyFrame: HullFrame) {
  return {
    neckY: fit.bodyNeckT / bodyFrame.scale + bodyFrame.offset.y,
    axisX: bodyFrame.offset.x,
    axisZ: fit.dz / bodyFrame.scale + bodyFrame.offset.z,
    unit: 1 / bodyFrame.scale,
  };
}

/**
 * 파츠를 뼈 둘(neck·head)에 묶는다. 턱 위는 head 100%, 목 아래는 neck 100%, 사이는 부드럽게.
 * 캡슐 스키닝을 다시 돌리지 않는 이유: 파츠는 머리 하나라 뼈 둘이면 충분하고, 본체 스켈레톤을
 * 그대로 공유해야 애니메이션이 한 벌로 돈다.
 */
export function skinPartToHead(geometry: THREE.BufferGeometry, skeleton: THREE.Skeleton, neckY: number, headY: number): void {
  const pos = geometry.getAttribute('position') as THREE.BufferAttribute;
  const idxOf = (name: string) => Math.max(0, skeleton.bones.findIndex((b) => b.name === name));
  const iNeck = idxOf('neck'), iHead = idxOf('head');
  const si = new Uint16Array(pos.count * 4);
  const sw = new Float32Array(pos.count * 4);
  const lo = neckY, hi = Math.max(neckY + 1e-4, headY);
  for (let v = 0; v < pos.count; v++) {
    const x = Math.min(1, Math.max(0, (pos.getY(v) - lo) / (hi - lo)));
    const t = x * x * (3 - 2 * x);
    si[v * 4] = iHead; sw[v * 4] = t;
    si[v * 4 + 1] = iNeck; sw[v * 4 + 1] = 1 - t;
  }
  geometry.setAttribute('skinIndex', new THREE.BufferAttribute(si, 4));
  geometry.setAttribute('skinWeight', new THREE.BufferAttribute(sw, 4));
}

/**
 * 본체의 머리를 접어 넣는다 — 새 머리 안에 숨기려고. 목 위로 갈수록 축(x=axisX, z=axisZ) 쪽으로 당기고
 * **동시에 목 쪽으로 낮춘다**. 옆으로만 오므리면 턱과 정수리가 새 머리를 뚫고 나온다(실측: 턱 밑 검은 얼룩).
 * 이음매 부근은 건드리지 않는다(부드러운 falloff). 위상은 그대로라 스킨 가중치를 다시 굽지 않아도 된다.
 */
export function shrinkBodyHead(
  geometry: THREE.BufferGeometry,
  anchors: { neckY: number; axisX: number; axisZ: number; unit: number },
  amount = 0.35,
  band = 0.04,
  flatten = 0.45,
): void {
  const pos = geometry.getAttribute('position') as THREE.BufferAttribute;
  const bandY = band * anchors.unit;
  for (let v = 0; v < pos.count; v++) {
    const y = pos.getY(v);
    const x = Math.min(1, Math.max(0, (y - anchors.neckY) / bandY));
    const t = x * x * (3 - 2 * x);
    if (t <= 0) continue;
    const f = 1 - amount * t;
    pos.setX(v, anchors.axisX + (pos.getX(v) - anchors.axisX) * f);
    pos.setZ(v, anchors.axisZ + (pos.getZ(v) - anchors.axisZ) * f);
    pos.setY(v, anchors.neckY + (y - anchors.neckY) * (1 - flatten * t));
  }
  pos.needsUpdate = true;
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
}
